import dayjs, {type Dayjs} from 'dayjs';
import React, {useEffect, useState} from 'react';

import HDate from '../helpers/HDate';
import {updateState, updateStateAttribute} from '../helpers/helper';
import CLocalizedText from './CLocalizedText';
import CustomDatePicker from './CustomDatePicker';

interface GenericDateRangeSelectorProps {
  minDate?: number;
  maxDate?: number;
  initStartDate: number | undefined;
  initEndDate: number | undefined;
  onChangeDeprecate?: (obj: Partial<Record<'start' | 'end', number>>) => void;
  onChange?: (startUnix: number, endUnix: number) => void;
  onValidationErrorDeprecate?: (hasErrors: boolean) => void;
  limitWidth?: boolean;
  allowSameDate?: true;
  minDateSpan?: number;
  inlineScroll?: boolean;
  onlyEndDate?: boolean;
}

interface State {
  startDateErrorMessage?: JSX.Element;
  endDateErrorMessage?: JSX.Element;
  startDate: Dayjs | undefined;
  endDate: Dayjs | undefined;
}

/***
 *
 * @param minDate min date as UNIX timestamp in milliseconds
 * @param maxDate max date as UNIX timestamp in milliseconds
 * @param start UNIX timestamp in milliseconds
 * @param limitWidth boolean to limit the width
 * @constructor
 */
export default function GenericDateRangeSelector({
                                                   minDate,
                                                   maxDate,
                                                   initStartDate,
                                                   initEndDate,
                                                   onChangeDeprecate,
                                                   onChange,
                                                   limitWidth,
                                                   minDateSpan,
                                                   inlineScroll,
                                                   /**
                                                    * @deprecated
                                                    * This prop is marked as deprecated and will be removed in future
                                                    */
                                                   onlyEndDate = false
                                                 }: GenericDateRangeSelectorProps) {
  const classNames = ['w-full'];

  if (limitWidth === undefined || limitWidth) {
    classNames.push('sm:w-44');
  }

  const wrapperClasses = inlineScroll
      ? 'flex flex-row flex-nowrap gap-y-4 gap-x-2 overflow-x-auto'
      : 'flex gap-4 gap-y-2 flex-wrap flex-grow sm:flex-grow-0';

  const [state, setState] = useState<State>({startDate: dayjs(initStartDate), endDate: dayjs(initEndDate)});
  const {endDateErrorMessage} = state;

  const validateDate = (startDate: number | undefined, endDate: number | undefined) => {
    let startDateErrorMessage: JSX.Element | undefined;
    let endDateErrorMessage: JSX.Element | undefined;

    if (minDateSpan && dayjs(endDate).diff(startDate, 'day') <= minDateSpan - 1) {
      startDateErrorMessage = (
          <span className={'field-error-message'}>
          <CLocalizedText inline dictKey={'errorDateSpanLessThan1'}/>
          <span> {minDateSpan} </span>
          <CLocalizedText inline dictKey={'errorDateSpanLessThan2'}/>
        </span>
      );
      endDateErrorMessage = (
          <span className={'field-error-message'}>
          <CLocalizedText inline dictKey={'errorDateSpanLessThan1'}/>
          <span> {minDateSpan} </span>
          <CLocalizedText inline dictKey={'errorDateSpanLessThan2'}/>
        </span>
      );
    }

    if (dayjs(endDate).isBefore(startDate, 'day')) {
      startDateErrorMessage = undefined;
      endDateErrorMessage = <CLocalizedText dictKey={'errorEndDateBeforeStart'}/>;
    }

    if (dayjs(startDate).isBefore(minDate, 'day')) {
      startDateErrorMessage = (
          <span className={'field-error-message'}>
          <CLocalizedText inline dictKey={'errorStartDateIsBefore1'}/>
          <span> {HDate.getShortDate(new Date(minDate ?? 0))} </span>
          <CLocalizedText inline dictKey={'errorStartDateIsBefore2'}/>
        </span>
      );
    }

    if (dayjs(endDate).isAfter(maxDate, 'day')) {
      endDateErrorMessage = (
          <span className={'field-error-message'}>
          <CLocalizedText inline dictKey={'errorEndDateIsAfter1'}/>
          <span> {HDate.getShortDate(new Date(maxDate ?? 0))} </span>
          <CLocalizedText inline dictKey={'errorEndDateIsAfter2'}/>
        </span>
      );
    }

    if (startDateErrorMessage ?? endDateErrorMessage) {
      updateState<State>({startDateErrorMessage, endDateErrorMessage}, state, setState);
      return false;
    } else {
      updateState<State>({startDateErrorMessage: undefined, endDateErrorMessage: undefined}, state, setState);
      return true;
    }
  };

  const minDateDayjs = minDate ? dayjs(minDate) : undefined;
  const maxDateDayjs = maxDate ? dayjs(maxDate) : undefined;

  const handleChange = (isStartDate: boolean, newValue: Dayjs) => {
    let startDate = state.startDate;
    let endDate = state.endDate;
    if (isStartDate) {
      startDate = newValue;
    } else {
      endDate = newValue;
    }
    if (!startDate || !endDate) return;
    updateState<State>({startDate, endDate}, state, setState);
    const startUnix = startDate.unix() * 1000;
    const endUnix = endDate.unix() * 1000;
    if (!validateDate(startUnix, endUnix)) return;
    if (!newValue.isValid() || newValue.isBefore(minDate) || newValue.isAfter(maxDate)) return;
    if (onChange && startDate && endDate) onChange(startUnix, endUnix);
  };

  useEffect(() => {
    updateStateAttribute<State>({startDate: dayjs(initStartDate), endDate: dayjs(initEndDate)}, setState);
  }, [initStartDate, initEndDate]);

  if (onlyEndDate) {
    return (
        <div className={wrapperClasses}>
          <div className={`w-full flex-col ${inlineScroll ? '' : 'sm:w-[250px]'}`}>
            <CustomDatePicker
                label="Date"
                value={state.endDate}
                minDate={minDate ? dayjs(minDate) : undefined}
                maxDate={maxDate ? dayjs(maxDate) : undefined}
                className={classNames.join(' ')}
                isError={!!endDateErrorMessage}
                onChange={(e) => {
                  const unix = dayjs(e).unix() * 1000;
                  setState({...state, endDate: e});
                  if (onChangeDeprecate) onChangeDeprecate({end: unix});
                }}
            />
            {endDateErrorMessage ? (
                <div className="text-red-600 text-[0.75rem] mt-1 ml-1 flex gap-1">
                  {endDateErrorMessage}
                </div>
            ) : null}
          </div>
        </div>
    );
  }
  return (
      <div className={wrapperClasses}>
        <div className={`w-full flex-col ${inlineScroll ? '' : 'sm:w-[250px]'}`}>
          <CustomDatePicker
              label={(<CLocalizedText dictKey={'fieldStartDate'}/>) as unknown as string}
              value={state.startDate}
              minDate={minDateDayjs}
              maxDate={maxDateDayjs}
              className={classNames.join(' ')}
              onChange={(e) => {
                if (onChangeDeprecate) onChangeDeprecate({start: dayjs(e).unix() * 1000});
                handleChange(true, e);
              }}
              isError={!!state.startDateErrorMessage}
              // helperText={state.startDateErrorMessage}
          />
          {state.startDateErrorMessage ? (
              <div className="text-red-600 text-[0.75rem] mt-1 ml-1 flex gap-1">{state.startDateErrorMessage}</div>
          ) : null}
        </div>
        <div className={`w-full flex-col ${inlineScroll ? '' : 'sm:w-[250px]'}`}>
          <CustomDatePicker
              label={(<CLocalizedText dictKey={'fieldEndDate'}/>) as unknown as string}
              value={state.endDate}
              minDate={minDateDayjs}
              maxDate={maxDateDayjs}
              isError={!!endDateErrorMessage}
              className={classNames.join(' ')}
              onChange={(e) => {
                if (onChangeDeprecate) onChangeDeprecate({end: dayjs(e).unix() * 1000});
                handleChange(false, e);
              }}
          />
          {state.endDateErrorMessage ? (
              <div className="text-red-600 text-[0.75rem] mt-1 ml-1 flex gap-1">{state.endDateErrorMessage}</div>
          ) : null}
        </div>
      </div>
  );
}
